import SelectContactModal from "@/components/features/contact/SelectContactModal/SelectContactModal";
import { useUser } from "@/components/UserProvider";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { Input } from "@/components/ui/shadcn/Input/Input";
import { Label } from "@/components/ui/shadcn/Label/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/Select/Select";
import { getUsersOptions } from "@/lib/data/react-query";
import { useFirmPhones } from "@/lib/data/react-query/firm/firm-phones";
import {
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
  formatUsersName,
} from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import {
  newParticipant,
  type EditableParticipant,
} from "./phone-call-log-utils";

export function PhoneCallParticipantsField({
  contactId,
  participants,
  setParticipants,
}: {
  contactId: string;
  participants: EditableParticipant[];
  setParticipants: React.Dispatch<React.SetStateAction<EditableParticipant[]>>;
}) {
  const { currentUser } = useUser();
  const { data: usersData } = useQuery(getUsersOptions());
  const users = usersData?.data ?? [];
  const { data: firmPhonesData } = useFirmPhones();
  const firmPhones = firmPhonesData?.data ?? [];
  const [selectingContactKey, setSelectingContactKey] = useState<string>();

  const updateParticipant = (
    key: string,
    update: Partial<EditableParticipant>,
  ) => {
    setParticipants((current) =>
      current.map((participant) =>
        participant.key === key ? { ...participant, ...update } : participant,
      ),
    );
  };

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label>Other participants</Label>
            <p className="text-muted-foreground text-xs">
              The contact is always included automatically.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setParticipants((current) => [...current, newParticipant()]);
            }}
          >
            <Plus /> Add
          </Button>
        </div>
        {participants.map((participant, index) => (
          <div
            key={participant.key}
            className="bg-muted/30 grid gap-3 rounded-lg border p-3 sm:grid-cols-[160px_1fr_auto]"
          >
            <Select
              value={participant.kind}
              onValueChange={(value) => {
                const kind = value as EditableParticipant["kind"];
                updateParticipant(participant.key, {
                  kind,
                  value: kind === "USER" ? currentUser.id : "",
                  phoneNumber: kind === "USER" ? currentUser.phoneNumber : "",
                  displayLabel: undefined,
                });
                if (kind === "CONTACT") {
                  setSelectingContactKey(participant.key);
                }
              }}
            >
              <SelectTrigger aria-label={`Participant ${index + 1} type`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USER">Firm user</SelectItem>
                <SelectItem value="FIRM_PHONE">Firm phone</SelectItem>
                <SelectItem value="EXTERNAL">External number</SelectItem>
                <SelectItem value="CONTACT">Contact</SelectItem>
              </SelectContent>
            </Select>

            {participant.kind === "USER" ? (
              <Select
                value={participant.value || currentUser.id}
                onValueChange={(value) => {
                  const user = users.find((item) => item.id === value);
                  updateParticipant(participant.key, {
                    value,
                    phoneNumber: user?.phoneNumber ?? "",
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {participant.value &&
                  !users.some((user) => user.id === participant.value) ? (
                    <SelectItem value={participant.value}>
                      {participant.value === currentUser.id
                        ? (formatUsersName(currentUser) ?? "Current user")
                        : "Firm user"}
                    </SelectItem>
                  ) : null}
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {formatUsersName(user)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : participant.kind === "FIRM_PHONE" ? (
              <Select
                value={participant.value}
                onValueChange={(value) => {
                  const phone = firmPhones.find((item) => item.id === value);
                  updateParticipant(participant.key, {
                    value,
                    phoneNumber: phone?.number ?? "",
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a firm phone" />
                </SelectTrigger>
                <SelectContent>
                  {firmPhones.map((phone) => (
                    <SelectItem key={phone.id} value={phone.id}>
                      {phone.label}:{" "}
                      {formatLocalOrIntlPhoneNumber(phone.number)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : participant.kind === "CONTACT" ? (
              <Button
                type="button"
                variant="outline"
                className="justify-start font-normal"
                onClick={() => {
                  setSelectingContactKey(participant.key);
                }}
              >
                {participant.displayLabel ||
                  (participant.value ? "Selected contact" : "Select a contact")}
              </Button>
            ) : (
              <Input
                value={participant.phoneNumber}
                onChange={(event) => {
                  updateParticipant(participant.key, {
                    phoneNumber: event.target.value,
                  });
                }}
                placeholder="Phone number"
                aria-label={`Participant ${index + 1} phone number`}
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Remove participant ${index + 1}`}
              disabled={participants.length === 1}
              onClick={() => {
                setParticipants((current) =>
                  current.filter((item) => item.key !== participant.key),
                );
              }}
            >
              <X />
            </Button>
          </div>
        ))}
      </div>

      <SelectContactModal
        open={Boolean(selectingContactKey)}
        setOpen={(nextOpen) => {
          if (!nextOpen) setSelectingContactKey(undefined);
        }}
        title="Select call participant"
        description="Select an additional contact who participated in this call."
        excludeContactIds={[
          contactId,
          ...participants
            .filter(
              (participant) =>
                participant.kind === "CONTACT" &&
                participant.key !== selectingContactKey &&
                participant.value,
            )
            .map((participant) => participant.value),
        ]}
        onSelectContact={(contact) => {
          if (!selectingContactKey) return;
          const primaryPhone = contact.phones.find((item) => item.isPrimary);
          const phone = primaryPhone
            ? primaryPhone.number
            : (contact.phones.at(0)?.number ?? "");
          updateParticipant(selectingContactKey, {
            value: contact.id,
            phoneNumber: phone,
            displayLabel: formatContactsName(contact) ?? "Contact",
          });
          setSelectingContactKey(undefined);
        }}
      />
    </>
  );
}
